import { useEffect, useState, type FormEvent } from "react";

import { useLanguage } from "@/i18n/LanguageContext";
import { adminUpdateSettings, fetchSettings, type ConferenceSettings } from "../api";
import { PageHeader } from "../shells/PageHeader";

function toLocalInput(iso: string | null | undefined) {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function AdminSettingsPage() {
  const { t } = useLanguage();
  const [conf, setConf] = useState<ConferenceSettings | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    fetchSettings().then(setConf).catch(() => setConf(null));
  }, []);

  if (!conf) return <p>{t.common.loading}</p>;

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const body: Record<string, unknown> = {};
    for (const [k, v] of fd.entries()) {
      if (typeof v === "string") body[k] = v;
    }
    body.registration_open = fd.get("registration_open") === "on";
    body.paper_submission_open = fd.get("paper_submission_open") === "on";
    body.reminders_enabled = fd.get("reminders_enabled") === "on";
    for (const key of ["card_width_mm", "card_height_mm", "card_safe_margin_mm"] as const) {
      const raw = fd.get(key);
      if (typeof raw === "string" && raw.trim()) body[key] = Number(raw);
    }
    const res = await adminUpdateSettings(body as Partial<ConferenceSettings>);
    setConf(res.data);
    setMsg(t.admin.saved);
  };

  return (
    <div>
      <PageHeader title={t.admin.settings} backTo="/admin" />
      {msg && <div className="dn-alert dn-alert-ok">{msg}</div>}
      <form className="dn-form" onSubmit={onSubmit} style={{ maxWidth: 720 }}>
        {(
          [
            ["name_en", conf.name_en],
            ["name_ar", conf.name_ar],
            ["theme_en", conf.theme_en],
            ["theme_ar", conf.theme_ar],
            ["venue_name_en", conf.venue_name_en],
            ["venue_name_ar", conf.venue_name_ar],
            ["address_en", conf.address_en],
            ["address_ar", conf.address_ar],
            ["contact_email", conf.contact_email],
            ["phone_primary", conf.phone_primary],
            ["phone_secondary", conf.phone_secondary || ""],
            ["director_name_en", conf.director_name_en],
            ["director_name_ar", conf.director_name_ar],
            ["map_url", conf.map_url || ""],
            ["hero_announcement_en", conf.hero_announcement_en || ""],
            ["hero_announcement_ar", conf.hero_announcement_ar || ""],
            ["timezone", conf.timezone],
            ["general_card_title", conf.general_card_title || "General Conference Participation Card"],
            ["participant_card_title", conf.participant_card_title || "Conference Participant Card"],
            [
              "hotel_card_title",
              conf.hotel_card_title || "Conference Participant Card + Hotel Access",
            ],
            ["card_brand_name", conf.card_brand_name || ""],
            ["card_letterhead_path", conf.card_letterhead_path || ""],
            ["card_width_mm", String(conf.card_width_mm ?? 80)],
            ["card_height_mm", String(conf.card_height_mm ?? 120)],
            ["card_safe_margin_mm", String(conf.card_safe_margin_mm ?? 5)],
          ] as const
        ).map(([name, value]) => (
          <label key={name}>
            {name}
            <input name={name} defaultValue={value} />
          </label>
        ))}
        <p className="dn-muted" style={{ marginTop: "-0.5rem", marginBottom: "1rem" }}>
          {t.admin.cardSettingsHelp}
        </p>
        <p className="dn-muted" style={{ marginTop: "-0.75rem", marginBottom: "1rem" }}>
          {t.admin.cardPrintHelp}
        </p>
        <label>
          start_at
          <input name="start_at" type="datetime-local" defaultValue={toLocalInput(conf.start_at)} />
        </label>
        <label>
          end_at
          <input name="end_at" type="datetime-local" defaultValue={toLocalInput(conf.end_at)} />
        </label>
        <label>
          abstract_deadline
          <input
            name="abstract_deadline"
            type="datetime-local"
            defaultValue={toLocalInput(conf.abstract_deadline)}
          />
        </label>
        <label>
          paper_deadline
          <input
            name="paper_deadline"
            type="datetime-local"
            defaultValue={toLocalInput(conf.paper_deadline)}
          />
        </label>
        <label>
          <span>registration_open</span>
          <input name="registration_open" type="checkbox" defaultChecked={conf.registration_open} />
        </label>
        <label>
          <span>paper_submission_open</span>
          <input
            name="paper_submission_open"
            type="checkbox"
            defaultChecked={conf.paper_submission_open}
          />
        </label>
        <label>
          <span>{t.admin.remindersEnabled}</span>
          <input
            name="reminders_enabled"
            type="checkbox"
            defaultChecked={conf.reminders_enabled !== false}
          />
        </label>
        <button className="dn-btn dn-btn-primary" type="submit">
          {t.admin.save}
        </button>
      </form>
    </div>
  );
}
